import { CustomFields, Entity, HexColor, Location } from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { RoutalDefaultColors } from '@nx-smartmonkey/ui';
import { VehicleMapper } from '../../mappers/vehicle/VehicleMapper';

import configuration from '../../services/configuration';
import { Driver } from '../driver/Driver';

const defaultStartTime = 8 * 60 * 60; // Default start time is 8:00
const defaultEndTime = 17 * 60 * 60; // Default end time is 17:00

const defaultTimeWindow: TimeWindow = [defaultStartTime, defaultEndTime];
export interface VehicleUpdateProps {
  external_id?: string;
  label?: string;
  phone?: string;
  email?: string;
  plate?: string;
  vehicle_model?: string;
  color?: HexColor;
  brand?: string;
  comments?: string;
  default_time_window?: TimeWindow;
  default_max_weight?: number;
  default_max_volume?: number;
  default_max_services?: number;
  default_max_distance?: number;
  default_min_distance?: number;
  default_max_time?: number;
  default_min_time?: number;
  default_start_location?: Location | null;
  default_end_location?: Location | null;
  default_provides?: string[];
  default_geo_fences?: string[];
  custom_fields?: CustomFields;
  price_per_distance?: number;
  price_per_minute?: number;
}

export interface VehicleProps {
  organization_id: string;
  project_id: string;
  avatar_url?: string;
  external_id?: string;
  label?: string;
  phone?: string;
  email?: string;
  plate?: string;
  vehicle_model?: string;
  color?: HexColor;
  brand?: string;
  comments?: string;
  default_time_window?: TimeWindow;
  default_max_weight?: number;
  default_max_volume?: number;
  default_max_services?: number;
  default_max_distance?: number;
  default_min_distance?: number;
  default_max_time?: number;
  default_min_time?: number;
  default_start_location?: Location | null;
  default_end_location?: Location | null;
  default_provides?: string[];
  default_geo_fences?: string[];
  custom_fields?: CustomFields;
  price_per_distance?: number;
  price_per_minute?: number;
  enabled: boolean;
  deleted?: boolean;
  created_by?: string;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
}

export class Vehicle extends Entity<VehicleProps> {
  get id(): string {
    return this._id!;
  }
  get organization_id(): string {
    return this.props.organization_id;
  }
  set organization_id(organization_id: string) {
    this.props.organization_id = organization_id;
  }
  get project_id(): string {
    return this.props.project_id;
  }
  set project_id(project_id: string) {
    this.props.project_id = project_id;
  }
  get avatar_url(): string | undefined {
    return this.props.avatar_url;
  }
  set avatar_url(avatar_url: string | undefined) {
    this.props.avatar_url = avatar_url;
  }
  get external_id(): string | undefined {
    return this.props.external_id;
  }
  set external_id(external_id: string | undefined) {
    this.props.external_id = external_id;
  }
  get label(): string | undefined {
    return this.props.label;
  }
  set label(label: string | undefined) {
    this.props.label = label;
  }
  get phone(): string | undefined {
    return this.props.phone;
  }
  set phone(phone: string | undefined) {
    this.props.phone = phone;
  }
  get email(): string | undefined {
    return this.props.email;
  }
  set email(email: string | undefined) {
    this.props.email = email;
  }
  get plate(): string | undefined {
    return this.props.plate;
  }
  set plate(plate: string | undefined) {
    this.props.plate = plate;
  }
  get vehicle_model(): string | undefined {
    return this.props.vehicle_model;
  }
  set vehicle_model(vehicle_model: string | undefined) {
    this.props.vehicle_model = vehicle_model;
  }
  get color(): HexColor | undefined {
    return this.props.color;
  }
  set color(color: HexColor | undefined) {
    this.props.color = color;
  }
  get brand(): string | undefined {
    return this.props.brand;
  }
  set brand(brand: string | undefined) {
    this.props.brand = brand;
  }
  get comments(): string | undefined {
    return this.props.comments;
  }
  set comments(comments: string | undefined) {
    this.props.comments = comments;
  }
  get default_time_window(): TimeWindow | undefined {
    return this.props.default_time_window;
  }
  set default_time_window(default_time_window: TimeWindow | undefined) {
    this.props.default_time_window = default_time_window;
  }
  get default_max_weight(): number | undefined {
    return this.props.default_max_weight;
  }
  set default_max_weight(default_max_weight: number | undefined) {
    this.props.default_max_weight = default_max_weight;
  }
  get default_max_volume(): number | undefined {
    return this.props.default_max_volume;
  }
  set default_max_volume(default_max_volume: number | undefined) {
    this.props.default_max_volume = default_max_volume;
  }
  get default_max_services(): number | undefined {
    return this.props.default_max_services;
  }
  set default_max_services(default_max_services: number | undefined) {
    this.props.default_max_services = default_max_services;
  }
  get default_max_distance(): number | undefined {
    return this.props.default_max_distance;
  }
  set default_max_distance(default_max_distance: number | undefined) {
    this.props.default_max_distance = default_max_distance;
  }
  get default_min_distance(): number | undefined {
    return this.props.default_min_distance;
  }
  set default_min_distance(default_min_distance: number | undefined) {
    this.props.default_min_distance = default_min_distance;
  }
  get default_max_time(): number | undefined {
    return this.props.default_max_time;
  }
  set default_max_time(default_max_time: number | undefined) {
    this.props.default_max_time = default_max_time;
  }
  get default_min_time(): number | undefined {
    return this.props.default_min_time;
  }
  set default_min_time(default_min_time: number | undefined) {
    this.props.default_min_time = default_min_time;
  }
  get default_start_location(): Location | undefined | null {
    return this.props.default_start_location;
  }
  set default_start_location(default_start_location: Location | undefined | null) {
    this.props.default_start_location = default_start_location;
  }
  get default_end_location(): Location | undefined | null {
    return this.props.default_end_location;
  }
  set default_end_location(default_end_location: Location | undefined | null) {
    this.props.default_end_location = default_end_location;
  }
  get default_provides(): string[] | undefined {
    return this.props.default_provides;
  }
  set default_provides(default_provides: string[] | undefined) {
    this.props.default_provides = default_provides;
  }
  get default_geo_fences(): string[] | undefined {
    return this.props.default_geo_fences;
  }
  set default_geo_fences(default_geo_fences: string[] | undefined) {
    this.props.default_geo_fences = default_geo_fences;
  }
  get custom_fields(): CustomFields | undefined {
    return this.props.custom_fields;
  }
  set custom_fields(custom_fields: CustomFields | undefined) {
    this.props.custom_fields = custom_fields;
  }
  get price_per_distance(): number | undefined {
    return this.props.price_per_distance;
  }
  set price_per_distance(price_per_distance: number | undefined) {
    this.props.price_per_distance = price_per_distance;
  }
  get price_per_minute(): number | undefined {
    return this.props.price_per_minute;
  }
  set price_per_minute(price_per_minute: number | undefined) {
    this.props.price_per_minute = price_per_minute;
  }
  get enabled(): boolean {
    return this.props.enabled;
  }
  get deleted(): boolean | undefined {
    return this.props.deleted;
  }
  get created_by(): string | undefined {
    return this.props.created_by;
  }
  set created_by(created_by: string | undefined) {
    this.props.created_by = created_by;
  }
  get created_at(): Date | undefined {
    return this.props.created_at;
  }
  set created_at(created_at: Date | undefined) {
    this.props.created_at = created_at;
  }
  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }
  set updated_at(updated_at: Date | undefined) {
    this.props.updated_at = updated_at;
  }
  get deleted_at(): Date | undefined {
    return this.props.deleted_at;
  }
  set deleted_at(deleted_at: Date | undefined) {
    this.props.deleted_at = deleted_at;
  }
  update(props: VehicleUpdateProps) {
    removeEmpty(props);

    if (props.external_id !== undefined) this.props.external_id = props.external_id;
    if (props.label !== undefined) this.props.label = props.label;
    if (props.phone !== undefined) this.props.phone = props.phone;
    if (props.email !== undefined) this.props.email = props.email;
    if (props.plate !== undefined) this.props.plate = props.plate;
    if (props.vehicle_model !== undefined) this.props.vehicle_model = props.vehicle_model;
    if (props.color !== undefined) this.props.color = props.color;
    if (props.brand !== undefined) this.props.brand = props.brand;
    if (props.comments !== undefined) this.props.comments = props.comments;
    if (props.default_time_window !== undefined) this.props.default_time_window = props.default_time_window;
    if (props.default_max_weight !== undefined) this.props.default_max_weight = props.default_max_weight;
    if (props.default_max_volume !== undefined) this.props.default_max_volume = props.default_max_volume;
    if (props.default_max_services !== undefined) this.props.default_max_services = props.default_max_services;
    if (props.default_max_distance !== undefined) this.props.default_max_distance = props.default_max_distance;
    if (props.default_min_distance !== undefined) this.props.default_min_distance = props.default_min_distance;
    if (props.default_max_time !== undefined) this.props.default_max_time = props.default_max_time;
    if (props.default_min_time !== undefined) this.props.default_min_time = props.default_min_time;
    if (props.default_start_location !== undefined) this.props.default_start_location = props.default_start_location;
    if (props.default_end_location !== undefined) this.props.default_end_location = props.default_end_location;
    if (props.default_provides !== undefined) this.props.default_provides = props.default_provides;
    if (props.default_geo_fences !== undefined) this.props.default_geo_fences = props.default_geo_fences;
    if (props.custom_fields !== undefined) this.props.custom_fields = props.custom_fields;
    if (props.price_per_distance !== undefined) this.props.price_per_distance = props.price_per_distance;
    if (props.price_per_minute !== undefined) this.props.price_per_minute = props.price_per_minute;

    this.props.updated_at = new Date();
  }
  private constructor(props: VehicleProps, id?: string) {
    super(props, id);
  }
  static create(props: VehicleProps, id?: string): Vehicle {
    return new Vehicle(props, id);
  }

  /**
   * Creates the admin link for inspection.
   * @param {vehicleId} - Project vehicle id.
   * @return {string} - Admin link.
   */
  getAsVehicleLink(token: string): string {
    const path = `admin?token=${encodeURIComponent(token)}&as_vehicle=${this.id}`;
    const adminLink = `${configuration.DRIVERS_ENDPOINT}/${path}`;
    return adminLink;
  }

  driverFromVehicle(): Driver {
    return VehicleMapper.toDriver(this);
  }

  static getDefaultColor(vehicles: Vehicle[]) {
    const usedColors = vehicles.map((vehicle) => vehicle.color?.value);
    const unusedColors = RoutalDefaultColors.filter((color) => !usedColors.includes(color));

    if (unusedColors.length > 0) {
      return unusedColors[0];
    }

    return RoutalDefaultColors[Math.floor(Math.random() * RoutalDefaultColors.length)];
  }

  static getDefaultTimeWindow(): TimeWindow {
    return [...defaultTimeWindow];
  }
}
